import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { ThemeProvider } from "@emotion/react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import KMKTable from "../../Common/Table/KMKTable";
import ReportService from "../../../service/report";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import MultiSelect from "../../../util/multiselect";
import useWindowResize from "../../../hooks/useWindowsReszie";
import { createTheme } from "../../MyWorkspace/Detail/overview/theme";
import Workspace from "../../../service/workspace";
import { convertTimezone } from "../../../util/common";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    backgroundColor: "#e8edf1",
    color: "black",
    margin: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    display: "flex",
    flexWrap: "wrap",
    padding: "0px !important",
  },
  HeaderTitle: {
    fontSize: 20,
    display: "inline-flex",

    "& span": {
      marginLeft: "5px",
    },
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    width: "100%",
    overflow: "hidden",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "35px",
      marginLeft: "-2px",
    },
  },
  Title: { display: "flex", flex: "1", alignItems: "center", paddingRight: "16px" },
}));

const theme = createTheme();

const DetailTable = () => {
  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(510);

  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState([]);
  const [count, setCount] = useState(0);
  const [workspaceList, setWorkspaceList] = useState([]);

  const options = useRef({
    limit: 10,
    page: 0,
    orderBy: "Name",
    order: "asc",
    workspaces: [],
  });

  const getUsageReport = useCallback(async () => {
    const { order, orderBy, page, limit, workspaces } = options.current;

    setLoading(true);
    setUsage([]);

    const sageMaker = new ReportService();
    const { success, data } = await sageMaker.getDataAITopWorkspaces({
      order,
      orderBy,
      page,
      limit,
      ...(workspaces.length === workspaceList.length ? {} : { workspaceIds: workspaces.join(",") }),
    });
    if (success) {
      setUsage(data.workspaceList);
      setCount(data.count);
    }
    setLoading(false);
  }, [workspaceList.length]);

  useEffect(() => {
    getUsageReport();
  }, []);

  useEffect(() => {
    new Workspace().getAllWorkspaces().then((response) => {
      if (response.success) {
        options.current.workspaces = response.data.map(({ Workspace_id }) => Workspace_id);
        setWorkspaceList(response.data.map(({ Workspace_id, Name }) => ({ Name, label: Workspace_id })));
      }
    });
  }, []);

  const handleSorting = useCallback(
    (property) => {
      const isAsc = options.current.orderBy === property && options.current.order === "asc";

      options.current.orderBy = property;
      options.current.order = isAsc ? "desc" : "asc";

      getUsageReport();
    },
    [getUsageReport]
  );

  const reportColumns = useMemo(
    () => [
      {
        id: "Name",
        name: "WORKSPACE NAME",
        sortable: true,
        style: { maxWidth: 160, wordBreak: "break-word" },
        align: "left",
        width: 160,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val,
      },
      {
        id: "NumOfChats",
        name: "TOTAL CHATS",
        sortable: true,
        style: { maxWidth: 120 },
        align: "center",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val,
      },
      {
        id: "NumOfMessages",
        name: "TOTAL MESSAGES",
        sortable: true,
        style: { maxWidth: 120 },
        align: "center",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val,
      },
      {
        id: "NumOfQueries",
        name: "TOTAL QUERIES",
        sortable: true,
        style: { maxWidth: 120 },
        align: "center",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val,
      },
      {
        id: "LastMessageDate",
        name: "LAST MODIFIED",
        style: { maxWidth: 140 },
        sortable: true,
        align: "center",
        width: 140,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => convertTimezone(val),
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      options.current.page = newPage;

      getUsageReport();
    },
    [getUsageReport]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      options.current.limit = parseInt(event.target.value, 10);
      options.current.page = 0;

      getUsageReport();
    },
    [getUsageReport]
  );

  const handleWorkspaceChange = useCallback(
    (selectionList) => {
      options.current.workspaces = selectionList;

      getUsageReport();
    },
    [getUsageReport]
  );

  return (
    <>
      <Toolbar className={classes.Header}>
        <Box className={classes.Title}>
          <Typography className={classes.HeaderTitle} component="div">
            Data AI Usage
          </Typography>
        </Box>
        <MultiSelectDropDown
          dataid="id"
          variant="standard"
          placeHolder={<MultiSelect title="Workspace" dataLength={options.current.workspaces.length} />}
          options={workspaceList}
          selected={options.current.workspaces}
          isSelectAll={true}
          disabled={loading}
          onSelectChange={handleWorkspaceChange}
          basedOn="label"
          searchOn="Name"
          allowSearching
        />
        <Box>
          <Tooltip placement="top" title="Refresh" arrow>
            <span>
              <IconButton disabled={loading} onClick={getUsageReport}>
                <RefreshOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Toolbar>

      <ThemeProvider theme={theme}>
        <Paper className={classes.DataTableContainer}>
          <KMKTable
            loading={loading}
            columns={reportColumns}
            containerHeight={tableHeight}
            noDataText={"No records found"}
            data={usage}
            sortingProperties={{
              order: options.current.order,
              orderBy: options.current.orderBy,
              handleSorting,
            }}
            boldHeader
            tableProps={{ stickyHeader: true }}
            pagination={true}
            paginationProperties={{
              rowsPerPageOptions: [10, 25, 50, 100],
              count,
              rowsPerPage: options.current.limit,
              page: options.current.page,
              onPageChange: handleChangePage,
              handleChangeRowsPerPage: handleChangeRowsPerPage,
              component: "div",
              className: classes.pagination,
              "data-id": "StewardQueueTablePagination",
            }}
            dataIdKey="SageMakerReportID"
          />
        </Paper>
      </ThemeProvider>
    </>
  );
};

export default DetailTable;
